import React, { createContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { authApi } from '../services/api/authApi';
import logger from '../services/loggerService';
import { AuthenticatedUser, AuthContextType, User } from '../types';
import { AUTH_SESSION_KEYS } from '../constants/securityConstants';
import apiClient, {
  setToken as setApiToken,
  clearToken as clearApiToken,
  markSessionExpired,
  LegacyApiClientOptions,
} from '../utils/apiClient';
import { BrowserStorageSanitizer } from '../services/BrowserStorageSanitizer';
import { useSessionSync } from '../hooks/useSessionSync';
import { useNotificationStore } from '../stores/useNotificationStore';
import { useThemeStore } from '../stores/useThemeStore';
import { useUIStateStore } from '../stores/useUIStateStore';

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Cross-tab session syncer using native React hook
  const { broadcastTermination } = useSessionSync(user?.id, () => {
    logger.info('Cross-tab logout signal detected. Tearing down local session.', { context: 'AuthContext' });
    sessionStorage.removeItem(AUTH_SESSION_KEYS.REFRESH_FAILED);
    clearApiToken();
    setUser(null);
    // Strictly purge unwhitelisted local storage to prevent cross-tenant data leaks
    BrowserStorageSanitizer.purgeAll();
  });

  // Dynamically sync theme and UI stores with logged in user context
  useEffect(() => {
    if (user) {
      useThemeStore.getState().initTheme(user.id, user.preferences?.themeMode);
      useUIStateStore.getState().initUIState(user.id, user.preferences?.uiSettings);
    } else {
      useThemeStore.getState().initTheme(null);
      useUIStateStore.getState().initUIState(null);
    }
  }, [user?.id, user?.preferences?.themeMode, user?.preferences?.uiSettings]);

  /**
   * Initializes authentication state on app mount.
   * Uses sessionStorage flag to prevent infinite refresh loops
   * when the refresh token has already failed in this browser session.
   *
   * @security sessionStorage is used instead of localStorage:
   *           - Cleared automatically when browser/tab closes
   *           - Prevents persisting failed state across sessions
   *           - New browser session gets a fresh start
   */
  const initializeAuth = useCallback(async () => {
    try {
      // Check if refresh has already failed in this browser session
      // This prevents infinite loops when page reloads after redirect
      const refreshFailed = sessionStorage.getItem(AUTH_SESSION_KEYS.REFRESH_FAILED);
      if (refreshFailed === 'true') {
        logger.debug('Skipping refresh - already failed in this session', { context: 'AuthContext' });
        setUser(null);
        clearApiToken();
        setIsLoading(false);
        return;
      }

      // Try to get a new access token using the HttpOnly refresh token cookie
      const { accessToken } = await authApi.refresh();

      if (accessToken) {
        // If we get a token, fetch the user's profile
        setApiToken(accessToken);
        const userData = await authApi.me();

        // Combine into the AuthenticatedUser object for the context
        const authenticatedUser: AuthenticatedUser = { ...userData, token: accessToken };
        setUser(authenticatedUser);

        // Re-seed encryption key with explicit userId now that profile is loaded.
        // The initial setApiToken seeds via JWT extraction; this explicit call
        // ensures deterministic seeding from the authoritative user record.
        setApiToken(accessToken, userData.id);
      }
    } catch (error) {
      // Mark that refresh has failed for this browser session
      // This prevents infinite retry loops on subsequent page loads
      sessionStorage.setItem(AUTH_SESSION_KEYS.REFRESH_FAILED, 'true');

      // Silent in production to prevent information leakage
      // In development, helpful for debugging auth flows
      if (import.meta.env.DEV) {
        logger.info('No active session found', { context: 'AuthContext' });
      }
      setUser(null);
      clearApiToken();
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  /**
   * Logs in a user and sets their session.
   * Clears the refresh failed flag to enable future refresh attempts.
   *
   * @security Clears failed flag on successful login to allow legitimate sessions
   */
  const login = (userData: AuthenticatedUser) => {
    // Clear the failed flag on successful login
    // This enables refresh attempts for the new session
    sessionStorage.removeItem(AUTH_SESSION_KEYS.REFRESH_FAILED);
    // Pass userId explicitly — the auth layer is the authoritative source of user identity
    setApiToken(userData.token, userData.id);
    setUser(userData);
  };

  /**
   * Logs out the user and clears all session state.
   * Clears the refresh failed flag to allow fresh login attempts.
   *
   * @security Clears all auth state including sessionStorage flag
   * @security Calls backend logout to invalidate server-side tokens
   */
  const logout = useCallback(async () => {
    try {
      // Call backend logout FIRST while the token is still valid
      await authApi.logout();
    } catch (error) {
      logger.error(
        'Logout API call failed, but logging out client-side anyway.',
        error,
        'AuthContext'
      );
    } finally {
      // CRITICAL: Mark session expired IMMEDIATELY after backend call to block
      // all concurrent/pending API calls from widgets that re-mount during
      // the React tree teardown. This is the primary defense against 401 cascades.
      markSessionExpired();

      // Clear the failed flag on explicit logout
      // This allows fresh login attempts without the "already failed" guard
      sessionStorage.removeItem(AUTH_SESSION_KEYS.REFRESH_FAILED);
      clearApiToken();
      setUser(null);

      //strictly purge all unwhitelisted local storage to prevent cross-tenant data leaks
      BrowserStorageSanitizer.purgeAll();

      //strictly purge all in-memory notification state to prevent state bleed between user sessions
      useNotificationStore.getState().reset();

      // broadcast logout to other open tabs to ensure global teardown
      broadcastTermination();
      
      // No need to navigate here, ProtectedRoute will handle it.
    }
  }, []);

  const fetchWithAuth = useCallback(
    async <T = unknown,>(
      endpoint: string,
      options: LegacyApiClientOptions = {}
    ): Promise<T> => {
      return await apiClient<T>(endpoint, options);
    },
    []
  );

  const updateUser = useCallback((userData: AuthenticatedUser | User) => {
    setUser((prev) => {
      if (!prev) return null;
      // If userData has a token, it's an AuthenticatedUser, use it full
      if ('token' in userData) {
        return userData as AuthenticatedUser;
      }
      // Otherwise it's just User data, preserve the existing token
      return { ...userData, token: prev.token } as AuthenticatedUser;
    });
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, logout, isLoading, setIsLoading, fetchWithAuth, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};
